import type { ProjectListItem } from "@nlc/shared-types";
import type { ColumnDef } from "@tanstack/react-table";

import { memo, useCallback, useMemo, useState } from "react";

import { DraggableTable as Table } from "@/components/table";
import { ProjectStatusBadge } from "@/features/shared/components/ProjectStatusBadge";
import { useDebounce } from "@/hooks/useDebounce";
import { dateFormatterWithConditionalYear } from "@/utils/dateFormatterWithConditionalYear";

import { useGetTrashedProjects } from "../hooks/useGetTrashedProjects";

interface TrashedAccountsTableProps {
  search?: string;
}

const NameCell = memo(function NameCell({
  row,
}: {
  row: { original: ProjectListItem };
}) {
  return (
    <div className="font-medium text-text-primary text-sm leading-tight truncate">
      {row.original.name.name}
    </div>
  );
});

const OwnerCell = memo(function OwnerCell({
  row,
}: {
  row: { original: ProjectListItem };
}) {
  return (
    <div className="font-medium text-text-primary text-sm leading-tight truncate">
      {row.original.owner.name}
    </div>
  );
});

const ProductCell = memo(function ProductCell({
  row,
}: {
  row: { original: ProjectListItem };
}) {
  return (
    <div className="font-medium text-text-primary text-sm leading-tight truncate">
      {row.original.product.name}
    </div>
  );
});

const StatusCell = memo(function StatusCell({
  row,
}: {
  row: { original: ProjectListItem };
}) {
  return <ProjectStatusBadge status={row.original.status} />;
});

const CreatedAtCell = memo(function DeletedAtCell({
  row,
}: {
  row: { original: ProjectListItem };
}) {
  return (
    <span className="text-text-tertiary text-sm truncate">
      {row.original.createdAt
        ? dateFormatterWithConditionalYear(row.original.createdAt)
        : "-"}
    </span>
  );
});

const DeletedAtCell = memo(function DeletedAtCell({
  row,
}: {
  row: { original: ProjectListItem };
}) {
  return (
    <span className="text-text-tertiary text-sm truncate">
      {row.original.deletedAt
        ? dateFormatterWithConditionalYear(row.original.deletedAt)
        : "-"}
    </span>
  );
});

const DeletedByCell = memo(function DeletedByCell({
  row,
}: {
  row: {
    original: ProjectListItem;
  };
}) {
  const deletedBy = row.original.deletedBy;

  if (!deletedBy) {
    return <span className="text-text-tertiary text-sm truncate">-</span>;
  }

  return (
    <div className="flex items-center gap-2 min-w-0">
      {deletedBy.profilePicture && (
        <img
          alt={deletedBy.name}
          className="size-6 rounded-full object-cover flex-shrink-0"
          src={deletedBy.profilePicture}
        />
      )}
      <span className="text-text-primary font-medium text-sm truncate">
        {deletedBy.name}
      </span>
    </div>
  );
});

export const TrashedAccountsTable = memo(function TrashedAccountsTable({
  search,
}: TrashedAccountsTableProps) {
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const debouncedQuery = useDebounce(search, 500, () => setPage(1));

  const { data, isLoading } = useGetTrashedProjects({
    itemsPerPage,
    page,
    search: debouncedQuery,
  });

  const columns: ColumnDef<ProjectListItem>[] = useMemo(
    () => [
      {
        accessorKey: "name",
        cell: ({ row }) => <NameCell row={row} />,
        enableSorting: false,
        header: "Account Name",
        size: 250,
      },
      {
        accessorKey: "owner",
        cell: ({ row }) => <OwnerCell row={row} />,
        enableSorting: false,
        header: "Owner",
        size: 200,
      },
      {
        accessorKey: "product",
        cell: ({ row }) => <ProductCell row={row} />,
        enableSorting: false,
        header: "Product",
        size: 150,
      },
      {
        accessorKey: "status",
        cell: ({ row }) => <StatusCell row={row} />,
        enableSorting: false,
        header: "Status",
        size: 200,
      },
      {
        accessorKey: "createdAt",
        cell: ({ row }) => <CreatedAtCell row={row} />,
        enableSorting: false,
        header: "Created on",
        size: 150,
      },
      {
        accessorKey: "deletedAt",
        cell: ({ row }) => <DeletedAtCell row={row} />,
        enableSorting: false,
        header: "Deleted on",
        size: 150,
      },
      {
        accessorKey: "deletedBy",
        cell: ({ row }) => <DeletedByCell row={row} />,
        enableSorting: false,
        header: "Deleted by",
        size: 150,
      },
    ],
    [],
  );

  const paginationConfig = useMemo(() => {
    if (!data?.metadata) return;

    return {
      metadata: {
        currentPage: data.metadata.currentPage,
        itemsPerPage: data.metadata.itemsPerPage,
        totalItems: data.metadata.totalItems,
        totalPages: data.metadata.totalPages,
      },
      onItemsPerPageChange: (newItemsPerPage: number) => {
        setItemsPerPage(newItemsPerPage);
        setPage(1);
      },
      onPageChange: (newPage: number) => setPage(newPage),
      pageSizeOptions: [10, 25, 50, 100],
      showPageSizeSelector: true,
    };
  }, [data]);

  const getRowId = useCallback((project: ProjectListItem) => project.id, []);

  if (!isLoading && (!data?.projects || data.projects.length === 0)) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <p className="text-text-secondary">No deleted accounts found</p>
      </div>
    );
  }

  return (
    <div className="rounded-b-xl bg-bg-primary overflow-auto trashed-table">
      <style>{`
        .trashed-table tbody tr {
          height: 60px !important;
        }
        .trashed-table tbody td {
          max-width: 0;
          overflow: hidden;
        }
        .trashed-table tbody td > div,
        .trashed-table tbody td > span,
        .trashed-table tbody td > a {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          display: block;
        }
      `}</style>
      <Table
        className="rounded-b-xl w-full"
        columns={columns}
        data={data?.projects || []}
        getRowId={getRowId}
        isLoading={isLoading}
        pagination={paginationConfig}
      />
    </div>
  );
});
